(() => {
  if (typeof document === 'undefined') return;

  const MOBILE = '(max-width: 749px)';
  const ENGRAVING_LABEL = /deckel[\s-]*gravur/i;
  const TRAY_SIGNATURE_INPUT = 'input[name="signature_reloading_tray"]';
  const INTERACTIVE = 'input, textarea, select, [contenteditable="true"]';

  const visible = element => Boolean(element && element.getClientRects().length && !element.hidden);
  const productSubmitButton = (scope, sectionId) => {
    const selector = sectionId
      ? `#ProductSubmitButton-${CSS.escape(sectionId)}`
      : '[id^="ProductSubmitButton-"]';
    const buttons = [...scope.querySelectorAll(selector)];
    return buttons.find(button => visible(button) && !button.disabled)
      || buttons.find(visible)
      || buttons.find(button => !button.disabled)
      || buttons[0];
  };
  const visibleModal = element => {
    if (!visible(element)) return false;
    if (element.closest('details:not([open])')) return false;
    const style = getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0
      && rect.bottom > 0 && rect.right > 0
      && rect.top < window.innerHeight && rect.left < window.innerWidth;
  };
  const clean = value => (value || '').replace(/\s+/g, ' ').trim();
  const normalizeGloboPrices = info => {
    const config = window.GPOConfigs;
    const currency = config?.cart?.currency || config?.shop?.currency;
    const german = (document.documentElement.lang || '').toLowerCase().startsWith('de');
    if (!german || currency !== 'EUR') return;
    if (config) config.money_format = '{{amount_with_comma_separator}} €';
    info.querySelectorAll('[id^="price-"] .price-item').forEach(item => {
      if (item.childElementCount) return;
      const normalized = item.textContent.replace(/€\s*([0-9][0-9.\s]*,[0-9]{2})\s*EUR/g, '$1\u00a0€');
      if (normalized !== item.textContent) item.textContent = normalized;
    });
  };
  const outsideViewport = (rect, viewportTop = 0, viewportBottom = window.innerHeight, wasOutside = false) => {
    if (!rect || rect.bottom <= rect.top || viewportBottom <= viewportTop) return false;
    const overlap = Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, viewportTop);
    // A small dead band prevents scrollbar/address-bar jitter from alternating display states.
    if (wasOutside) return overlap < Math.min(20, (rect.bottom - rect.top) / 2);
    return rect.bottom <= viewportTop - 12 || rect.top >= viewportBottom + 12;
  };
  const visibleLabelText = label => {
    if (!label) return '';
    const clone = label.cloneNode(true);
    clone.querySelectorAll('.visually-hidden, [hidden], [aria-hidden="true"], .fm-variant-option-price').forEach(node => node.remove());
    return clean(clone.textContent);
  };

  const optionLabel = info => {
    const selected = [...info.querySelectorAll('variant-selects select')]
      .map(select => select.options[select.selectedIndex]?.textContent)
      .filter(Boolean);
    if (selected.length) return selected.map(clean).join(' · ');
    return [...info.querySelectorAll('variant-selects input:checked + label, variant-radios input:checked + label')]
      .map(visibleLabelText)
      .join(' · ');
  };

  const openForInvalid = event => {
    const details = event.target.closest('[data-fm-purchase-extras]');
    if (!details) return;
    details.open = true;
    requestAnimationFrame(() => event.target.focus({ preventScroll: false }));
  };

  const revealExtras = (details, field) => {
    if (!details || details.hidden) return;
    details.open = true;
    if (field) requestAnimationFrame(() => field.focus({ preventScroll: false }));
  };

  const optionAnchor = element => {
    if (ENGRAVING_LABEL.test(clean(element.textContent))) return 'engraving';
    if (element.querySelector(TRAY_SIGNATURE_INPUT)) return 'tray';
    return '';
  };

  const labelExtras = (details, mode) => {
    const summary = details?.querySelector('summary');
    if (!summary || summary.dataset.fmPersonalizationLabel === mode) return;
    const copy = document.createElement('span');
    copy.className = 'fm-purchase-flow__extras-copy';
    const title = document.createElement('span');
    title.className = 'fm-purchase-flow__extras-title';
    const german = (document.documentElement.lang || '').toLowerCase().startsWith('de');
    const hint = document.createElement('span');
    hint.className = 'fm-purchase-flow__extras-hint';
    if (mode === 'tray') {
      title.textContent = german ? 'Farbe & Personalisierung auswählen' : 'Choose color & personalization';
      hint.textContent = german
        ? 'Farbe und weitere Optionen für dein Wiederladebrett auswählen.'
        : 'Choose a color and other options for your reloading tray.';
    } else {
      title.textContent = german ? 'Gravur & Personalisierung auswählen' : 'Choose engraving & personalization';
      hint.textContent = german
        ? 'Deckel-Gravur, Wunschtext, Logo und weitere Optionen auswählen.'
        : 'Choose lid engraving, custom text, a logo, and more options.';
    }
    copy.append(title, hint);
    summary.replaceChildren(copy);
    summary.dataset.fmPersonalizationLabel = mode;
  };

  const checkedCustomizationNeedsExtras = info =>
    [...info.querySelectorAll('input[name="Customizing Options"]:checked')]
      .some(input => /(?:text|logo)/i.test(clean(input.value)));

  const arrangeOptions = (info, flow) => {
    const app = info.querySelector('.gpo-app');
    const originalButton = productSubmitButton(info);
    if (!app) {
      flow.dataset.optionsStatus = 'waiting-app';
      return false;
    }
    if (!originalButton) {
      flow.dataset.optionsStatus = 'waiting-button';
      return false;
    }

    const elements = [...app.querySelectorAll('.gpo-element')]
      .filter(element => !element.parentElement?.closest('.gpo-element'));
    if (!elements.length) {
      flow.dataset.optionsStatus = 'waiting-options';
      return false;
    }
    const primaryElements = elements.filter(optionAnchor);
    if (!primaryElements.length) {
      flow.dataset.optionsStatus = 'unmatched';
      return false;
    }
    const submitSlot = flow.querySelector('[data-fm-purchase-submit]');
    const extrasDetails = flow.querySelector('[data-fm-purchase-extras]');
    const extrasSlot = flow.querySelector('[data-fm-purchase-extras-body]');
    if (!submitSlot || !extrasDetails || !extrasSlot) {
      flow.dataset.optionsStatus = 'missing-flow-slots';
      return false;
    }
    const nativeForm = originalButton.closest('form[data-type="add-to-cart-form"]');
    if (!nativeForm || !nativeForm.contains(app)) {
      flow.dataset.optionsStatus = 'outside-form';
      return false;
    }

    const summaryMode = primaryElements.every(element => optionAnchor(element) === 'tray') ? 'tray' : 'engraving';
    labelExtras(extrasDetails, summaryMode);

    if (flow.parentElement !== app) app.append(flow);
    const optionGroups = [...new Set(elements.map(element => element.closest('.gpo-group__childs')).filter(Boolean))];
    if (!optionGroups.length || primaryElements.some(element => !optionGroups.includes(element.closest('.gpo-group__childs')))) {
      flow.dataset.optionsStatus = 'unmatched-group';
      return false;
    }
    optionGroups.forEach(group => {
      if (group.parentElement !== extrasSlot) extrasSlot.append(group);
    });
    if (originalButton.parentElement !== submitSlot) submitSlot.append(originalButton);
    const quantity = info.querySelector('.product-form__quantity');
    if (quantity && (quantity.parentElement !== submitSlot || quantity.nextElementSibling !== originalButton)) {
      submitSlot.insertBefore(quantity, originalButton);
    }
    submitSlot.classList.toggle('fm-purchase-flow__submit--quantity', Boolean(quantity));
    const hideExtras = optionGroups.length === 0;
    if (extrasDetails.hidden !== hideExtras) extrasDetails.hidden = hideExtras;
    if (!hideExtras && extrasDetails.nextElementSibling !== submitSlot) submitSlot.before(extrasDetails);
    flow.dataset.ready = 'true';
    flow.dataset.optionsStatus = 'ready';
    return true;
  };

  const init = root => {
    if (root.dataset.fmPurchaseReady === 'true') return;
    const sectionId = root.dataset.sectionId;
    const info = document.getElementById(`ProductInfo-${sectionId}`);
    const flow = root.querySelector('[data-fm-purchase-flow]');
    const sticky = root.querySelector('[data-fm-sticky-purchase]');
    if (!info || !flow || !sticky) return;

    root.dataset.fmPurchaseReady = 'true';
    info.addEventListener('invalid', openForInvalid, true);

    const sync = () => {
      // The Liquid-rendered starting price does not change with the selected variant.
      const stickyButton = sticky.querySelector('[data-fm-sticky-submit]');
      if (stickyButton.disabled) stickyButton.disabled = false;
    };

    const keyboardOpen = () => {
      const field = document.activeElement;
      if (!field || sticky.contains(field)) return false;
      return field.matches?.('textarea, select, [contenteditable="true"], input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="tel"], input[type="url"], input[type="number"], input[type="password"]');
    };
    const modalOpen = () => [...document.querySelectorAll('dialog[open], .modal[open], [role="dialog"][aria-modal="true"]')]
      .some(visibleModal);
    let revealedError = null;
    const revealAppErrors = (force = false) => {
      const details = flow.querySelector('[data-fm-purchase-extras]');
      const error = flow.querySelector(
        '[data-fm-purchase-extras-body] [aria-invalid="true"], [data-fm-purchase-extras-body] .gpo-element.has-error, [data-fm-purchase-extras-body] .gpo-invalid, [data-fm-purchase-extras-body] .gpo-error:not(:empty), [data-fm-purchase-extras-body] .gpo-error-message:not(:empty), [data-fm-purchase-extras-body] .help-block.error:not(:empty)'
      );
      if (!error) { revealedError = null; return; }
      const errorElement = error.matches?.('.gpo-element') ? error : error.closest?.('.gpo-element');
      const errorState = errorElement || error;
      if (!force && revealedError === errorState) return;
      revealedError = errorState;
      const fields = error.matches?.(INTERACTIVE) ? [error] : [...(errorElement?.querySelectorAll(INTERACTIVE) || [])];
      const field = fields.find(candidate => !candidate.disabled && visible(candidate))
        || errorElement?.querySelector('.help-block.error:not(:empty), .gpo-error:not(:empty), .gpo-error-message:not(:empty)')
        || errorElement
        || error;
      if (!field.matches?.(INTERACTIVE) && !field.hasAttribute('tabindex')) field.tabIndex = -1;
      revealExtras(details, field);
    };
    const setSticky = originalPassed => {
      const hidden = !matchMedia(MOBILE).matches || !originalPassed || keyboardOpen() || modalOpen();
      if (sticky.hidden !== hidden) sticky.hidden = hidden;
    };

    let originalPassed = false;
    let viewportFrame = 0;
    const observedSubmitButtons = new WeakSet();
    let submitVisibilityObserver = null;
    let visibilityButton = null;
    const updatePreviewOffset = () => {
      const previewBar = document.getElementById('PBarNextFrameWrapper');
      let offset = 0;
      if (previewBar && visibleModal(previewBar)) {
        const rect = previewBar.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom >= window.innerHeight) {
          offset = Math.max(0, Math.round(window.innerHeight - rect.top));
        }
      }
      const value = `${offset}px`;
      if (sticky.style.getPropertyValue('--fm-preview-offset') !== value) {
        sticky.style.setProperty('--fm-preview-offset', value);
      }
    };
    const updatePassedFromRect = () => {
      viewportFrame = 0;
      const button = productSubmitButton(info, sectionId);
      const viewportTop = window.visualViewport?.offsetTop || 0;
      const viewportBottom = viewportTop + (window.visualViewport?.height || window.innerHeight);
      originalPassed = Boolean(button && outsideViewport(button.getBoundingClientRect(), viewportTop, viewportBottom, originalPassed));
      updatePreviewOffset();
      setSticky(originalPassed);
    };
    const onViewportChange = () => {
      if (viewportFrame) return;
      viewportFrame = requestAnimationFrame(updatePassedFromRect);
    };
    const observeButton = () => {
      const button = productSubmitButton(info, sectionId);
      if (!button) return;
      if (!observedSubmitButtons.has(button)) {
        observedSubmitButtons.add(button);
        button.addEventListener('click', () => {
          const details = flow.querySelector('[data-fm-purchase-extras]');
          if (checkedCustomizationNeedsExtras(info)) revealExtras(details);
          requestAnimationFrame(() => revealAppErrors(true));
        }, true);
      }
      if (visibilityButton === button) return;
      submitVisibilityObserver?.disconnect();
      visibilityButton = button;
      // The observer only schedules a fresh read; it never commits stale entry geometry.
      submitVisibilityObserver = new IntersectionObserver(onViewportChange, { threshold: [0, 0.5, 1] });
      submitVisibilityObserver.observe(button);
    };

    sticky.querySelector('[data-fm-sticky-submit]').addEventListener('click', () => {
      const target = info.querySelector('variant-selects, variant-radios') || flow;
      if (!target.hasAttribute('tabindex')) target.tabIndex = -1;
      target.focus({ preventScroll: true });
      target.scrollIntoView({
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });

    let hydrationAttempts = 0;
    let hydrationTimer = 0;
    const scheduleHydrationRetry = () => {
      if (!root.isConnected || !info.isConnected || !flow.isConnected) return;
      if (!['waiting-app', 'waiting-button', 'waiting-options', 'error'].includes(flow.dataset.optionsStatus)) return;
      if (flow.dataset.ready === 'true' || hydrationTimer || hydrationAttempts >= 120) return;
      hydrationTimer = window.setTimeout(() => {
        hydrationTimer = 0;
        hydrationAttempts += 1;
        refresh();
      }, 500);
    };
    const refresh = () => {
      delete root.dataset.fmPurchaseError;
      normalizeGloboPrices(info);
      info.querySelectorAll('input[name="lid_style"]').forEach(input => {
        const label = input.closest('.gpo-swatches')?.querySelector('label');
        if (!label) return;
        const inner = label.querySelector('.gpo-swatch__inner');
        if (input.value === 'Kaliberbezeichnung' && inner) {
          inner.classList.add('fm-neutral-engraving');
          let text = inner.querySelector('.fm-neutral-engraving__text');
          if (!text) {
            text = document.createElement('span');
            text.className = 'fm-neutral-engraving__text';
            text.textContent = 'Dein Kaliber';
            inner.append(text);
          }
        }
        const addon = label.querySelector('.addOn')?.textContent.trim();
        let price = label.querySelector('.fm-option-surcharge');
        if (addon && !price) {
          price = document.createElement('span');
          price.className = 'fm-option-surcharge';
          label.append(price);
        }
        if (price && price.textContent !== (addon || '')) price.textContent = addon || '';
      });
      info.querySelectorAll('.ot-estimated-shipping-country-select-box-country').forEach(select => {
        if (!select.hasAttribute('aria-label')) select.setAttribute('aria-label', 'Lieferland');
        for (const option of select.options) {
          const name = { DE: 'Deutschland', CH: 'Schweiz', AT: 'Österreich' }[option.value];
          if (name && option.textContent !== name) option.textContent = name;
        }
      });
      let arranged = false;
      try {
        arranged = arrangeOptions(info, flow);
      } catch (error) {
        root.dataset.fmPurchaseError = 'options';
        flow.dataset.optionsStatus = 'error';
      }
      try {
        revealAppErrors();
      } catch (error) {
        root.dataset.fmPurchaseError = 'validation';
      }
      try {
        observeButton();
      } catch (error) {
        root.dataset.fmPurchaseError = 'observer';
      }
      try {
        sync();
      } catch (error) {
        root.dataset.fmPurchaseError = 'sync';
      }
      onViewportChange();
      if (arranged) {
        if (hydrationTimer) window.clearTimeout(hydrationTimer);
        hydrationTimer = 0;
      } else {
        scheduleHydrationRetry();
      }
    };
    let mutationRefreshFrame = 0;
    const scheduleMutationRefresh = () => {
      if (mutationRefreshFrame) return;
      mutationRefreshFrame = requestAnimationFrame(() => {
        mutationRefreshFrame = 0;
        refresh();
      });
    };
    const observer = new MutationObserver(mutations => {
      if (mutations.every(mutation => mutation.target.closest?.('[data-fm-sticky-purchase]'))) return;
      scheduleMutationRefresh();
    });
    observer.observe(info, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-invalid', 'class', 'hidden'],
    });
    document.addEventListener('focusin', () => setSticky(originalPassed));
    document.addEventListener('focusout', () => requestAnimationFrame(() => setSticky(originalPassed)));
    document.addEventListener('shopify:section:load', refresh);
    matchMedia(MOBILE).addEventListener?.('change', () => setSticky(originalPassed));
    window.addEventListener('scroll', onViewportChange, { passive: true });
    window.addEventListener('resize', onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('resize', onViewportChange, { passive: true });
    window.visualViewport?.addEventListener('scroll', onViewportChange, { passive: true });
    refresh();
    onViewportChange();
  };

  const boot = () => document.querySelectorAll('[data-fm-purchase-root]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot, { once: true }) : boot();
})();
